import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineCalendar } from "react-icons/hi";
import { twMerge } from "tailwind-merge";
import type { AgendaType } from "../../hooks/agenda";
import type { BusinessCardBooking } from "../../types/BusinessCardTypes";
import { AGENDA_PATH } from "../pages/dashboard/agenda/agendaTypes";
import OptionToggleRow from "./OptionToggleRow";

interface BusinessCardBookingSectionProps {
  booking?: BusinessCardBooking;
  /** Undefined while the types load, or when the viewer may not read them. */
  bookingTypes?: AgendaType[];
  canOfferBooking: boolean;
  /** The agenda is known to be off: the public card then has no button, whatever is chosen here. */
  agendaOff?: boolean;
  holder?: { first_name?: string; last_name?: string; email?: string } | null;
  onChange: (booking: BusinessCardBooking) => void;
}

/** Only a known list of types can tell that the pinned one is gone; offering all of them pins none. */
export const bookingTypeMissing = (
  booking: BusinessCardBooking | undefined,
  bookingTypes: AgendaType[] | undefined
) =>
  !!booking?.enabled &&
  !!booking.type_id &&
  bookingTypes !== undefined &&
  !bookingTypes.some((type) => type.id === booking.type_id);

const K = "permissions.businessCard.editModal.booking";

const nameOf = (holder: BusinessCardBookingSectionProps["holder"]) =>
  `${holder?.first_name ?? ""} ${holder?.last_name ?? ""}`.trim() ||
  holder?.email ||
  "";

const BusinessCardBookingSection: React.FC<BusinessCardBookingSectionProps> = ({
  booking,
  bookingTypes,
  canOfferBooking,
  agendaOff = false,
  holder,
  onChange,
}) => {
  const { t } = useTranslation();
  const lastPinned = useRef(booking?.type_id ?? null);

  if (!(canOfferBooking && holder) && !booking?.enabled) return null;

  const typesKnown = bookingTypes !== undefined;
  const typeMissing = bookingTypeMissing(booking, bookingTypes);
  const noTypeOffered =
    !agendaOff && typesKnown && bookingTypes.length === 0 && !booking?.enabled;
  const name = nameOf(holder);
  const offered = (typeId: string | null | undefined) =>
    typeId && bookingTypes?.some((type) => type.id === typeId) ? typeId : null;
  const pinsOne = !!booking?.type_id;
  const noneShown =
    !!booking?.enabled &&
    !pinsOne &&
    typesKnown &&
    !bookingTypes.some((type) => type.listed);
  const pin = (typeId: string) => {
    lastPinned.current = typeId;
    onChange({ enabled: true, type_id: typeId });
  };

  return (
    <section className="space-y-3 p-4 sm:p-6">
      <OptionToggleRow
        icon={HiOutlineCalendar}
        title={t("permissions.businessCard.editModal.booking.title")}
        description={t(
          "permissions.businessCard.editModal.booking.description"
        )}
        enabled={!!booking?.enabled}
        disabled={
          !booking?.enabled &&
          (agendaOff || noTypeOffered || (!typesKnown && !booking?.type_id))
        }
        onChange={(enabled) =>
          onChange({
            enabled,
            type_id:
              enabled && typesKnown
                ? offered(booking?.type_id)
                : booking?.type_id ?? null,
          })
        }
        className="pt-0"
      />
      {agendaOff && (
        <p className="text-xs text-gray-500">
          {t(`${K}.agendaOff`)}{" "}
          <Link
            to={AGENDA_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            {t(`${K}.openAgenda`)}
          </Link>
        </p>
      )}
      {noTypeOffered && (
        <p className="text-xs text-gray-500">
          {t("permissions.businessCard.editModal.booking.noTypeOffered", {
            name,
          })}{" "}
          <Link
            to="/control-panel/agenda?section=types"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            {t("permissions.businessCard.editModal.booking.openTypes")}
          </Link>
        </p>
      )}
      {booking?.enabled && !agendaOff && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-900">
            {t(`${K}.modeLabel`)}
          </legend>
          {(["all", "one"] as const).map((mode) => {
            const checked = mode === (pinsOne ? "one" : "all");
            const unavailable =
              mode === "one" && (!typesKnown || bookingTypes.length === 0);
            return (
              <label
                key={mode}
                className={twMerge(
                  "flex items-start gap-3 rounded-md border p-3",
                  checked ? "border-blue-300 bg-blue-50" : "border-gray-200",
                  unavailable && !checked
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                )}
              >
                <input
                  type="radio"
                  name="business-card-booking-mode"
                  value={mode}
                  checked={checked}
                  disabled={unavailable && !checked}
                  onChange={() =>
                    mode === "all"
                      ? onChange({ enabled: true, type_id: null })
                      : pin(
                          offered(lastPinned.current) ??
                            (bookingTypes?.[0]?.id as string)
                        )
                  }
                  className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    {t(mode === "all" ? `${K}.modeAll` : `${K}.modeOne`)}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {mode === "all"
                      ? t(`${K}.modeAllHelp`, { name })
                      : t(`${K}.modeOneHelp`)}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
      )}
      {noneShown && (
        <p className="text-xs text-gray-500">
          {t(`${K}.noTypeShown`, { name })}{" "}
          <Link
            to="/control-panel/agenda?section=types"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            {t(`${K}.openTypes`)}
          </Link>
        </p>
      )}
      {booking?.enabled && pinsOne && !typesKnown && (
        <p className="text-xs text-gray-500">
          {t("permissions.businessCard.editModal.booking.typeKept")}
        </p>
      )}
      {booking?.enabled && pinsOne && typesKnown && (
        <div className="space-y-1">
          <label
            htmlFor="business-card-booking-type"
            className="block text-sm font-medium text-gray-700"
          >
            {t("permissions.businessCard.editModal.booking.typeLabel")}
          </label>
          <select
            id="business-card-booking-type"
            value={typeMissing ? "" : booking.type_id ?? ""}
            onChange={(e) => pin(e.target.value)}
            className="block w-full rounded-md border border-gray-300 bg-white p-2.5 pr-8 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
          >
            <option value="" disabled>
              {t("permissions.businessCard.editModal.booking.typePlaceholder")}
            </option>
            {bookingTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.title}
              </option>
            ))}
          </select>
          {typeMissing ? (
            <p className="text-xs text-red-600" role="alert">
              {t("permissions.businessCard.editModal.booking.typeMissing")}
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              {t("permissions.businessCard.editModal.booking.typeHelper", {
                name,
              })}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default BusinessCardBookingSection;
